<template>
  <div class="event-detail-page">
    <template v-if="dateStatus === 'success'">
      <app-bar
        :title="eventDate.name"
        :show-back-button="true"
        :back-button-path="backPath"
      />

      <div
        class="container container-event-view"
        :class="{ 'has-fixed-action-button': actionButtonContainerFixed }"
      >
        <!-- Hero image -->
        <div class="hero-image-wrapper">
          <img
            v-if="hasEventImage"
            :src="eventDate.imageUrl"
            :alt="eventDate.name"
            class="hero-image"
            @error="onHeroImageError"
          />
          <div v-else class="hero-image hero-image-fallback">
            <i
              v-if="activityIcon"
              :class="activityIcon"
              class="hero-fallback-icon"
            />
          </div>
          <div class="hero-gradient" />
          <div
            v-if="eventDate.activity && activityIcon"
            class="hero-sport-badge"
          >
            <RoundIcon :size="28" :border-size="0" :icon-class="activityIcon" />
          </div>
        </div>
        <!-- Title + meta card -->
        <div class="meta-card">
          <div class="meta-card-header">
            <h1 class="meta-card-title min-w-0">{{ eventDate.name }}</h1>
            <div
              v-if="!eventDateIsFinished && !eventDate.cancelled"
              class="meta-card-share"
            >
              <button
                v-if="currentUserIsOwner"
                class="edit-btn"
                title="Event bewerken"
                @click="routeToEventEditView"
              >
                <i class="fal fa-pencil" />
                <span>Bewerken</span>
              </button>
              <a
                :href="`https://api.whatsapp.com/send?&text=${shareMessage}`"
                target="_blank"
                class="share-btn"
                title="Delen via WhatsApp"
                @click="trackShare"
              >
                <i class="fab fa-whatsapp" />
              </a>
              <button class="share-btn" title="Link kopieren" @click="copyLink">
                <i class="fal fa-link" />
              </button>
            </div>
          </div>
          <div v-if="groupName" class="meta-card-group" @click="routeToGroup">
            <i class="fal fa-users text-xs" />
            <span>{{ groupName }}</span>
          </div>

          <!-- Info grid 2x2 -->
          <div class="info-grid">
            <div class="info-grid-item">
              <i class="fal fa-calendar-alt info-grid-icon" />
              <span class="info-grid-text">{{ eventDate.date }}</span>
            </div>
            <div class="info-grid-item">
              <i class="fal fa-clock info-grid-icon" />
              <span class="info-grid-text">
                {{ eventDate.startDateTimeFormatted }}-{{
                  eventDate.endDateTimeFormatted
                }}
              </span>
            </div>
            <div class="info-grid-item">
              <i class="fal fa-building info-grid-icon" />
              <a
                v-if="locationLatLng"
                :href="
                  'https://www.google.com/maps/search/?api=1&query=' +
                  locationLatLng
                "
                target="_blank"
                class="info-grid-text truncate info-grid-link"
              >
                {{ eventDate.location.name }}
              </a>
              <span v-else class="info-grid-text truncate">
                {{ eventDate.location.name }}
              </span>
            </div>
            <div class="info-grid-item">
              <i class="fal fa-euro-sign info-grid-icon" />
              <span class="info-grid-text">
                <template v-if="eventDate.costFeeIncluded > 0">
                  {{ $n(eventDate.costFeeIncluded, 'currency') }}
                </template>
                <template v-else>Gratis</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Content grid -->
        <div class="content-grid">
          <!-- Left column -->
          <div class="content-left">
            <!-- Details -->
            <div class="content-card">
              <h3 class="content-card-title">Details</h3>
              <nl2br
                tag="p"
                :text="eventDate.description || ''"
                class="details-text"
              />
              <div v-if="eventDate.cancelled" class="alert alert-warning mt-2">
                Deze activiteit is geannuleerd, je kunt je dus niet meer
                aanmelden
              </div>
            </div>

            <!-- Reeks verlengen (ADR 0015): eigenaar, vanaf 7 dagen voor de laatste datum -->
            <div
              v-if="extendSeriesResult"
              class="alert alert-success"
              role="status"
              data-test="series-extend-success"
            >
              {{ extendSeriesResult }}
            </div>
            <div
              v-if="showExtendSeries"
              class="content-card"
              data-test="series-extend"
            >
              <h3 class="content-card-title">Reeks verlengen</h3>
              <p class="details-text">
                Deze reeks loopt tot en met {{ seriesLastDayLabel }}. Verleng
                hem met één tik tot en met {{ seriesExtendTargetLabel }}.
              </p>
              <div
                v-if="extendSeriesError"
                class="alert alert-danger mt-2"
                role="alert"
                data-test="series-extend-error"
              >
                {{ extendSeriesError }}
              </div>
              <button
                class="button button-primary w-full mt-3"
                :disabled="extendingSeries"
                data-test="series-extend-button"
                @click="extendSeries"
              >
                Verlengen tot {{ seriesExtendTargetLabel }}
              </button>
            </div>

            <!-- Info voor deelnemers (#196): de API stuurt dit veld alleen mee
                 naar wie het mag zien, dus geen eigen rechtencheck hier -->
            <div
              v-if="participantInfo"
              class="content-card participant-info-card"
              data-test="participant-info"
            >
              <h3 class="content-card-title">
                {{ $t('participantInfo.title') }}
              </h3>
              <p class="participant-info-hint">
                {{ $t('participantInfo.visibleHint') }}
              </p>
              <nl2br tag="p" :text="participantInfo" class="details-text" />
            </div>

            <!-- Berichten -->
            <div
              v-if="!eventDateIsFinished && !eventDate.cancelled"
              class="content-card"
            >
              <div class="flex items-center justify-between mb-3">
                <h3 class="content-card-title mb-0">Berichten</h3>
                <span class="text-gray-400" style="font-size: 0.72rem">
                  openbaar
                </span>
              </div>

              <div v-if="messages.length > 0" class="messages-list">
                <div
                  v-for="(msg, i) in displayedMessages"
                  :key="msg.id || i"
                  class="message-item"
                >
                  <initials-avatar
                    v-if="!msg.author.avatarUrl"
                    :first-name="msg.author.firstName || msg.author.name || ''"
                    :last-name="msg.author.lastName || ''"
                    :size="8"
                    class="message-avatar"
                  />
                  <div
                    v-else
                    class="message-avatar w-8 h-8 rounded-full bg-cover bg-center flex-shrink-0"
                    :style="{
                      backgroundImage: `url('${msg.author.avatarUrl}')`
                    }"
                  />
                  <div class="min-w-0">
                    <div class="flex items-baseline gap-2">
                      <span class="text-gray-700" style="font-size: 0.82rem">
                        {{ msg.author.name }}
                      </span>
                      <span class="text-gray-300" style="font-size: 0.7rem">
                        {{ formatDate(msg.sent_at) }}
                      </span>
                    </div>
                    <p class="text-gray-500 mt-0.5" style="font-size: 0.82rem">
                      {{ msg.content }}
                    </p>
                  </div>
                </div>
              </div>
              <p v-else class="text-gray-400 mb-3" style="font-size: 0.82rem">
                Nog geen berichten
              </p>
              <button
                v-if="hasMoreMessages"
                type="button"
                class="show-more-messages-btn"
                @click="showMoreMessages"
              >
                Toon meer berichten
              </button>

              <p
                v-if="!authId"
                class="text-center text-gray-400 mb-2"
                style="font-size: 0.78rem"
              >
                Log in om een bericht te plaatsen
              </p>
              <div v-if="authId" class="message-input-row">
                <input
                  v-model="messageInput"
                  type="text"
                  placeholder="Schrijf een bericht..."
                  class="message-input"
                  @keyup.enter="sendMessage"
                />
                <button class="message-send-btn" @click="sendMessage">
                  <i class="fal fa-paper-plane" />
                </button>
              </div>
            </div>
          </div>

          <!-- Right column: deelnemers -->
          <div class="content-card deelnemers-card">
            <div class="flex items-center justify-between mb-3">
              <button
                type="button"
                class="content-card-title mb-0 bg-transparent border-0 p-0 text-left hover:text-green-600 transition-colors cursor-pointer"
                @click="routeToParticipants()"
              >
                Deelnemers
              </button>
              <span class="text-gray-400" style="font-size: 0.76rem">
                {{ participantsAttendingYes.length }} van
                {{ eventDate.maximumParticipants }}
              </span>
            </div>

            <!-- Progress bar -->
            <div
              v-if="eventDate.maximumParticipants"
              class="flex items-center gap-2.5 mb-3"
            >
              <div
                class="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"
              >
                <div
                  class="h-full rounded-full"
                  :class="progressBarClass"
                  :style="{ width: `${Math.min(100, spotsRatio * 100)}%` }"
                />
              </div>
              <span
                class="px-2 py-0.5 rounded-full flex-shrink-0"
                :class="spotsBadgeClass"
                style="font-size: 0.72rem"
              >
                <template v-if="eventDate.isFull">Vol</template>
                <template v-else>
                  {{ spotsLeft }}
                  {{ spotsLeft === 1 ? 'plek' : 'plekken' }} over
                </template>
              </span>
            </div>

            <div class="deelnemers-list">
              <!-- Organisator -->
              <div v-if="organizer" class="deelnemer-item">
                <div class="deelnemer-avatar-wrap">
                  <div
                    v-if="organizer.avatarUrl"
                    class="deelnemer-avatar w-8 h-8 rounded-full bg-cover bg-center"
                    :style="{
                      backgroundImage: `url('${organizer.avatarUrl}')`
                    }"
                  />
                  <div v-else class="initials-avatar">
                    {{ getInitials(organizer.firstName, organizer.lastName) }}
                  </div>
                  <div class="organizer-crown">
                    <i class="fas fa-crown" style="font-size: 0.5rem" />
                  </div>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="text-gray-700" style="font-size: 0.84rem">
                    {{ organizer.firstName }} {{ organizer.lastName }}
                  </span>
                  <span class="organizer-badge">Organisator</span>
                </div>
              </div>

              <!-- Deelnemers -->
              <div
                v-for="(participant, index) in displayedParticipants"
                :key="index"
                class="deelnemer-item"
                :class="{
                  'deelnemer-current-user':
                    currentUserParticipant &&
                    participant.id === currentUserParticipant.id
                }"
              >
                <div
                  v-if="participant.avatarUrl"
                  class="deelnemer-avatar w-8 h-8 rounded-full bg-cover bg-center"
                  :class="{
                    'current-user-avatar':
                      currentUserParticipant &&
                      participant.id === currentUserParticipant.id
                  }"
                  :style="{
                    backgroundImage: `url('${participant.avatarUrl}')`
                  }"
                />
                <div
                  v-else
                  class="initials-avatar"
                  :class="{
                    'current-user-avatar':
                      currentUserParticipant &&
                      participant.id === currentUserParticipant.id
                  }"
                >
                  {{ getInitials(participant.firstName, participant.lastName) }}
                </div>
                <div class="flex items-center gap-1.5">
                  <span
                    :class="
                      currentUserParticipant &&
                      participant.id === currentUserParticipant.id
                        ? 'text-gray-900'
                        : 'text-gray-600'
                    "
                    style="font-size: 0.84rem"
                  >
                    {{ participant.firstName }}
                  </span>
                  <span
                    v-if="
                      currentUserParticipant &&
                      participant.id === currentUserParticipant.id
                    "
                    class="current-user-badge"
                  >
                    Jij
                  </span>
                </div>
              </div>

              <!-- Show all link -->
              <a
                v-if="participantsAttendingYes.length > 5"
                href="#"
                class="deelnemer-item show-all-link"
                @click.prevent="routeToParticipants()"
              >
                <div class="show-all-count">
                  +{{ participantsAttendingYes.length - 5 }}
                </div>
                <span>toon allemaal</span>
              </a>
            </div>

            <!-- Waitlist toggle — visible when the user can still act on the waitlist:
                 either the event is full, OR they are already on the waitlist
                 (covers the "spot freed up but user not yet promoted" case so
                 they can still leave the waitlist from this screen). -->
            <div
              v-if="
                waitlistEnabled &&
                !currentUserParticipant &&
                (eventDate.isFull || eventDate.isOnWaitlist)
              "
              class="waitlist-section"
            >
              <button
                v-if="eventDate.isOnWaitlist"
                class="waitlist-btn-on"
                @click="leaveWaitlist"
              >
                <i class="fal fa-bell" />
                Op wachtlijst &#10003;
              </button>
              <button v-else class="waitlist-btn-off" @click="joinWaitlist">
                <i class="fal fa-bell" />
                Plaats me op de wachtlijst
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky bottom CTA -->
      <div
        v-if="!eventDateIsFinished && !eventDate.cancelled"
        class="sticky-cta-container"
      >
        <div class="container sticky-cta-inner">
          <div v-if="attendanceLocked" class="attendance-locked text-center">
            <p class="text-gray-400 py-1 mb-0" style="font-size: 0.84rem">
              {{ $t('event.attendanceLocked.notice') }}
            </p>
            <a
              v-if="moreDates.length"
              href="#"
              class="cta-btn"
              @click.prevent="routeToEventSignup()"
            >
              {{ $t('event.attendanceLocked.otherDates') }}
            </a>
          </div>
          <a
            v-else-if="currentUserIsParticipant"
            href="#"
            class="cta-btn"
            @click.prevent="updateParticipantDate()"
          >
            aanwezigheid aanpassen
          </a>
          <a
            v-else-if="!eventDate.isFull"
            href="#"
            class="cta-btn"
            @click.prevent="updateParticipantDate()"
          >
            doe je mee?
          </a>
          <a
            v-else-if="eventDate.isFull && hasOtherAvailableDates"
            href="#"
            class="cta-btn"
            @click.prevent="routeToEventSignup()"
          >
            doe mee op een andere dag
          </a>
          <div
            v-else-if="eventDate.isFull"
            class="text-center text-gray-400 py-1"
            style="font-size: 0.84rem"
          >
            Deze datum is vol.
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="dateStatus === 'error'">
      <app-bar
        :title="'Event niet gevonden'"
        :show-back-button="true"
        :back-button-path="backPath"
      />
      <p class="text-center">
        <em>Het evenement bestaat niet of niet meer!</em>
      </p>
    </template>

    <template v-else-if="event">
      <app-bar title="" :show-back-button="true" :back-button-path="backPath" />
      <div
        class="container container-event-view"
        :class="{ 'has-fixed-action-button': actionButtonContainerFixed }"
      >
        <!-- Hero image for proposal -->
        <div class="hero-image-wrapper">
          <img
            v-if="event.imageUrl"
            :src="event.imageUrl"
            :alt="event.name"
            class="hero-image"
          />
          <div v-else class="hero-image hero-image-fallback">
            <i
              v-if="event.activity_type"
              :class="event.activity_type.activityIcon"
              class="hero-fallback-icon"
            />
          </div>
          <div class="hero-gradient" />
        </div>

        <div class="meta-card">
          <h1 class="meta-card-title">{{ event.name }}</h1>
          <div class="info-grid mt-4">
            <div class="info-grid-item">
              <i class="fal fa-building info-grid-icon" />
              <span class="info-grid-text truncate">
                {{ event.location.name }}
              </span>
            </div>
            <div class="info-grid-item">
              <i class="fal fa-euro-sign info-grid-icon" />
              <span class="info-grid-text">
                <template v-if="event.costFeeIncluded > 0">
                  {{ $n(event.costFeeIncluded, 'currency') }}
                </template>
                <template v-else>Gratis</template>
              </span>
            </div>
          </div>
        </div>

        <div class="content-card mt-2">
          <h3 class="content-card-title">Details</h3>
          <nl2br tag="p" :text="event.description || ''" class="details-text" />
          <div v-if="event.cancelled" class="alert alert-warning mt-2">
            Deze activiteit is geannuleerd, je kunt je dus niet meer aanmelden
          </div>
        </div>
      </div>
    </template>

    <spinner-loader v-if="loading || paymentStatusLoading" />
    <tab-bar selected="search" />

    <modal
      v-if="!loading && !paymentStatusLoading"
      :show="showFeedbackModal"
      :show-close-icon="true"
      @close="closeFeedbackModal"
    >
      <template
        v-if="(action === 'payment' && paymentSuccess) || action !== 'payment'"
        v-slot:header
      >
        Gelukt!
      </template>
      <template
        v-else-if="action === 'payment' && paymentPending"
        v-slot:header
      >
        In behandeling
      </template>
      <template v-else v-slot:header>Oeps...</template>
      <template v-if="action === 'payment'">
        <p v-if="paymentSuccess">
          Je betaling is ontvangen en je bent aangemeld voor deze activiteit!
        </p>
        <p v-else-if="paymentPending">
          Je betaling wordt nog verwerkt. Dit kan enkele minuten duren. Je
          ontvangt een bevestiging per e-mail zodra de betaling is afgerond en
          je bent aangemeld voor deze activiteit.
        </p>
        <p v-else-if="paymentFailed">
          Je betaling is helaas niet gelukt. Probeer het opnieuw of neem contact
          met ons op via contact@inviplay.nl.
        </p>
        <p v-else>
          We konden de status van je betaling niet ophalen. Controleer je e-mail
          voor een bevestiging of neem contact met ons op via
          contact@inviplay.nl.
        </p>
      </template>
      <template v-else-if="action === 'refund'">
        <p>
          Je aanwezigheid is bijgewerkt en je betaling wordt binnen vijf
          werkdagen teruggeboekt.
        </p>
      </template>
      <template v-else-if="action === 'signup'">
        <p>Je bent aangemeld voor deze activiteit!</p>
      </template>
      <template v-else>
        <p>Je hebt je aanwezigheid aangepast voor deze activiteit!</p>
      </template>
    </modal>
    <modal
      :show="showWaitlistModal"
      :show-close-icon="true"
      @close="showWaitlistModal = false"
    >
      <template v-slot:header>Gelukt!</template>
      <p>
        Je staat op de wachtlijst. We laten je weten zodra er een plekje
        vrijkomt.
      </p>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import { DateTime } from 'luxon'
import Nl2br from 'vue-nl2br'
import { mapGetters, mapState } from 'vuex'
import noImage from '@/assets/images/img_no-image.png'
import AppBar from '@/components/AppBar'
import TabBar from '@/components/TabBar'
import RoundIcon from '@/components/RoundIcon'
import SpinnerLoader from '@/components/SpinnerLoader'
import {
  EVENT_DATES_GET,
  EVENT_DATES_GET_ERROR,
  EVENT_JOIN_WAITLIST,
  EVENT_LEAVE_WAITLIST
} from '@/store/actions/date'
import { PAYMENT_GET_STATUS_BY_ID } from '@/store/actions/payment'
import { EVENT_VIEW } from '@/store/actions/event'
import api from '@/utils/api'
import InitialsAvatar from '@/components/InitialsAvatar'
import config from '@/utils/config'
import Modal from '../../components/Modal.vue'
import analytics from '@/utils/analytics'
import { OG_DEFAULT_IMAGE, truncate } from '@/utils/seo'
import { hasDateStarted } from '@/utils/attendance'
import {
  canExtendSeries,
  extendTargetDay,
  formatSeriesDay,
  lastSeriesDay,
  recurringErrorMessage,
  toDay
} from '@/utils/recurring'

export default {
  name: 'EventView',
  components: {
    AppBar,
    TabBar,
    RoundIcon,
    SpinnerLoader,
    Nl2br,
    InitialsAvatar,
    Modal
  },
  data() {
    return {
      showFeedbackModal: false,
      showWaitlistModal: false,
      messageInput: null,
      currentUserIsParticipant: false,
      datesAvailable: true,
      dates: [],
      profile: null,
      participantsAttendingYes: [],
      routeName: this.$route.name,
      securityHash: this.$route.params.securityHash,
      dateId: this.$route.params.dateId,
      eventId: this.$route.params.eventId,
      origin: this.$route.query.origin,
      paymentId: this.$route.query.pid,
      paymentStatusLoading: false,
      numberOfRepeats: 0,
      paymentStatusTimeout: null,
      paymentOverallTimeout: null,
      messages: [],
      messagesDisplayCount: 5,
      messagesPageSize: 10,
      groupName: null,
      extendingSeries: false,
      extendSeriesError: null,
      extendSeriesResult: null
    }
  },
  computed: {
    ...mapGetters(['authId']),
    ...mapState({
      participantProfile: (state) => state.participant.ownProfile,
      eventDate: (state) => state.date.eventDate,
      participants: (state) => state.date.participants,
      dateStatus: (state) => state.date.status,
      loading: (state) =>
        state.date.status === 'loading' || state.payment.loading,
      paymentStatus: (state) => state.payment.status,
      event: (state) => state.event.eventView
    }),
    participantInfo: function () {
      const info = this.eventDate && this.eventDate.participantInfo
      return typeof info === 'string' && info.trim() ? info : null
    },
    action: function () {
      return this.$route.query.action
    },
    backPath: function () {
      // origin=home komt van de homepage; die leeft op '/' (er is geen /home)
      if (!this.origin || this.origin === 'home') return '/'
      return `/${this.origin}`
    },
    eventImage: function () {
      return (this.eventDate && this.eventDate.imageUrl) || noImage
    },
    hasEventImage: function () {
      return (
        this.eventDate &&
        this.eventDate.imageUrl &&
        this.eventDate.imageUrl !== noImage
      )
    },
    showExtendSeries: function () {
      return canExtendSeries(this.eventDate, this.authId)
    },
    seriesLastDayLabel: function () {
      return formatSeriesDay(lastSeriesDay(this.eventDate.allDates))
    },
    seriesExtendTargetLabel: function () {
      return formatSeriesDay(extendTargetDay(this.eventDate.allDates))
    },
    currentUserIsOwner: function () {
      return this.eventDate && this.eventDate.ownerId === parseInt(this.authId)
    },
    actionButtonContainerFixed: function () {
      return (
        !!this.eventDate &&
        !this.eventDateIsFinished &&
        !this.eventDate.cancelled
      )
    },
    shareMessage: function () {
      if (!this.eventDate || !this.eventDate.name) return ''
      const url = this.buildShareUrl('whatsapp')
      return encodeURIComponent(
        `Doe je ook mee met ${this.eventDate.name}? Geef je op via inviplay: ${url}`
      )
    },
    numberOfNotFullDates: function () {
      return this.eventDate.allDates && this.eventDate.allDates.length
        ? this.eventDate.allDates.filter(
            (date) =>
              !date.isFull && moment(date.startDateTime).isAfter(moment())
          ).length
        : 0
    },
    // Signed up for a date that has started: no sign-out action any more.
    attendanceLocked: function () {
      return (
        !!this.currentUserParticipant &&
        hasDateStarted(this.eventDate.startDateTime)
      )
    },
    eventDateIsFinished: function () {
      return moment(this.eventDate.endDateTime).isBefore(moment())
    },
    moreDates: function () {
      return this.eventDate && this.eventDate.allDates
        ? this.eventDate.allDates.filter(
            (date) =>
              DateTime.fromISO(date.startDateTime) > DateTime.local() &&
              date.id !== this.eventDate.id
          )
        : []
    },
    paymentSuccess() {
      return this.paymentId && this.paymentStatus
        ? this.paymentStatus[this.paymentId] === 'paid' ||
            this.paymentStatus[this.paymentId] === 'completed'
        : false
    },
    paymentPending() {
      if (!this.paymentId || !this.paymentStatus) return false
      const status = this.paymentStatus[this.paymentId]
      return status === 'pending' || status === 'open' || status === 'created'
    },
    paymentFailed() {
      if (!this.paymentId || !this.paymentStatus) return false
      const status = this.paymentStatus[this.paymentId]
      return (
        status === 'expired' ||
        status === 'failed' ||
        status === 'canceled' ||
        status === 'cancelled'
      )
    },
    groupId() {
      return this.eventDate && this.eventDate.groupId
    },
    displayedMessages() {
      return this.messages.slice(0, this.messagesDisplayCount)
    },
    hasMoreMessages() {
      return this.messages.length > this.messagesDisplayCount
    },
    waitlistEnabled() {
      return config.featureFlags.waitlist && this.eventDate.waitlistEnabled
    },
    locationLatLng() {
      if (
        this.eventDate &&
        this.eventDate.location &&
        this.eventDate.location.coordinates
      ) {
        return (
          this.eventDate.location.coordinates.lat +
          ',' +
          this.eventDate.location.coordinates.lng
        )
      }
      return null
    },
    spotsRatio() {
      if (!this.eventDate.maximumParticipants) return 0
      return (
        this.participantsAttendingYes.length /
        this.eventDate.maximumParticipants
      )
    },
    spotsLeft() {
      return (
        this.eventDate.maximumParticipants -
        this.participantsAttendingYes.length
      )
    },
    progressBarClass() {
      if (this.eventDate.isFull) return 'bg-red-400'
      if (this.spotsRatio >= 0.8) return 'bg-yellow-400'
      return 'bg-green-500'
    },
    spotsBadgeClass() {
      if (this.eventDate.isFull) return 'bg-red-50 text-red-500'
      if (this.spotsRatio >= 0.8) return 'bg-yellow-50 text-yellow-600'
      return 'bg-green-50 text-green-600'
    },
    organizer() {
      if (!this.participants || !this.participants.length) return null
      return this.participants.find((p) => p.id === this.eventDate.ownerId)
    },
    currentUserParticipant() {
      if (!this.authId) return null
      return this.participantsAttendingYes.find(
        (p) => p.id === parseInt(this.authId)
      )
    },
    displayedParticipants() {
      const nonOwner = this.participantsAttendingYes.filter(
        (p) => !this.organizer || p.id !== this.organizer.id
      )
      if (!this.currentUserParticipant || this.currentUserIsOwner) {
        return nonOwner.slice(0, 4)
      }
      const others = nonOwner.filter(
        (p) => p.id !== this.currentUserParticipant.id
      )
      return [this.currentUserParticipant, ...others.slice(0, 3)]
    },
    activityIcon() {
      if (this.eventDate && this.eventDate.activity) {
        return (
          this.eventDate.activity.activityIcon || this.eventDate.activity.icon
        )
      }
      return null
    },
    hasOtherAvailableDates() {
      return (
        this.eventDate.allDates &&
        this.eventDate.allDates.length &&
        this.eventDate.allDates.some(
          (date) => !date.isFull && this.isAfterNow(date.endDateTime)
        )
      )
    }
  },
  watch: {
    groupId(groupId) {
      if (groupId) {
        this.messagesDisplayCount = 5
        this.getMessages()
        this.fetchGroupName(groupId)
      } else {
        this.groupName = null
      }
    },
    action(action) {
      this.showFeedbackModal =
        action && config.featureFlags.creditcards ? true : false
    },
    participantProfile(participantProfile) {
      this.profile = participantProfile
    },
    participants(participants) {
      if (participants.length) {
        this.currentUserIsParticipant = participants.some(
          (participant) => participant.id === parseInt(this.authId)
        )
        this.participantsAttendingYes = participants.filter(
          (participant) => participant.attendance === 'yes'
        )
      }
    },
    paymentStatus(paymentStatus) {
      const status = paymentStatus[this.paymentId]
      if (status === 'open' || status === 'pending' || status === 'created') {
        this.retryPaymentStatus()
      } else {
        this.stopPaymentPolling()
        this.paymentStatusLoading = false
        this.showFeedbackModal = true
      }
    }
  },
  created: async function () {
    if (this.action && config.featureFlags.creditcards) {
      this.showFeedbackModal = true
      if (this.action === 'payment' && this.paymentId) {
        this.paymentStatusLoading = true
        this.paymentOverallTimeout = setTimeout(() => {
          this.stopPaymentPolling()
          this.paymentStatusLoading = false
          this.showFeedbackModal = true
        }, 15000)
        this.$store.dispatch(PAYMENT_GET_STATUS_BY_ID, {
          paymentId: this.paymentId
        })
      }
    }

    if (this.routeName === 'EventView' && this.eventId && this.securityHash) {
      if (!this.dateId) {
        try {
          const response = await api.get(
            `event/${this.eventId}?securityHash=${this.securityHash}`
          )
          if (response && response.data && response.data.nextDate) {
            this.dateId = response.data.nextDate.id
          } else {
            this.$store.commit(EVENT_DATES_GET_ERROR)
          }
        } catch (e) {
          this.$store.commit(EVENT_DATES_GET_ERROR, e)
        }
      }
      this.$store.dispatch(EVENT_DATES_GET, {
        dateId: this.dateId,
        securityHash: this.securityHash
      })
      this.$router.push(
        `/events/${this.dateId}/${this.securityHash}${
          this.origin ? `?origin=${this.origin}` : ''
        }`
      )
    } else if (this.routeName === 'EventProposal' && this.eventId) {
      this.$store.dispatch(EVENT_VIEW, {
        eventID: this.eventId,
        securityHash: this.securityHash
      })
    } else {
      this.$store.dispatch(EVENT_DATES_GET, {
        dateId: this.dateId,
        securityHash: this.securityHash
      })
    }
  },
  mounted: async function () {
    this.getMessages()
  },
  beforeDestroy: function () {
    this.stopPaymentPolling()
  },
  methods: {
    async extendSeries() {
      if (this.extendingSeries) return
      this.extendingSeries = true
      this.extendSeriesError = null
      this.extendSeriesResult = null
      try {
        const response = await api.post(
          `event/${this.eventDate.eventId}/extendSeries`,
          {}
        )
        const result = (response && response.data) || {}
        const created = Array.isArray(result.createdDates)
          ? result.createdDates.length
          : Number(result.createdDates) || 0
        const until = result.endDate
          ? formatSeriesDay(toDay(result.endDate))
          : this.seriesExtendTargetLabel
        this.extendSeriesResult = `Reeks verlengd tot en met ${until}${
          created
            ? ` (${created} nieuwe ${created === 1 ? 'datum' : 'data'})`
            : ''
        }.`
        analytics.capture('series_extended', {
          event_id: this.eventDate.eventId,
          created_dates: created
        })
        this.$store.dispatch(EVENT_DATES_GET, {
          dateId: this.eventDate.id,
          securityHash: this.securityHash
        })
      } catch (e) {
        const apiError =
          e && e.response && e.response.data ? e.response.data.error : null
        this.extendSeriesError =
          recurringErrorMessage(apiError) ||
          'Het verlengen is niet gelukt. Probeer het later nog eens.'
      } finally {
        this.extendingSeries = false
      }
    },
    async fetchGroupName(groupId) {
      try {
        const response = await api.get(`/group/${groupId}`)
        if (response && response.data) {
          this.groupName = response.data.name
        }
      } catch (e) {
        console.log('error fetching group name', e)
      }
    },
    onHeroImageError(e) {
      e.target.style.display = 'none'
      e.target.parentElement.classList.add('hero-image-fallback-active')
    },
    formatDate(datetime) {
      return moment(datetime).fromNow()
    },
    getMessages: async function () {
      if (!this.groupId) return
      try {
        const messageRequest = await api.get(
          `/group/${this.groupId}/message?filter[order]=sent_at%20DESC`
        )
        if (messageRequest && messageRequest.data) {
          this.messages = messageRequest.data
        }
      } catch (e) {
        console.log('error loading messages', e)
      }
    },
    sendMessage: async function () {
      if (!this.messageInput || !this.groupId) return
      try {
        const result = await api.post(`/group/${this.groupId}/message`, {
          content: this.messageInput
        })
        if (result && result.data) {
          this.messagesDisplayCount += 1
          this.getMessages()
        }
      } catch (e) {
        console.log('error sending message', e)
      }
      this.messageInput = ''
    },
    showMoreMessages: function () {
      this.messagesDisplayCount += this.messagesPageSize
    },
    buildShareUrl(source) {
      const base = `${window.location.origin}/events/${this.dateId}/${this.securityHash}`
      return `${base}?utm_source=${source}&utm_medium=share&utm_campaign=event_share`
    },
    copyLink() {
      const url = this.buildShareUrl('copy_link')
      navigator.clipboard.writeText(url)
      analytics.capture('activity_shared', { method: 'copy_link' })
    },
    stopPaymentPolling: function () {
      if (this.paymentStatusTimeout) {
        clearTimeout(this.paymentStatusTimeout)
        this.paymentStatusTimeout = null
      }
      if (this.paymentOverallTimeout) {
        clearTimeout(this.paymentOverallTimeout)
        this.paymentOverallTimeout = null
      }
    },
    retryPaymentStatus: function () {
      if (this.numberOfRepeats < 10) {
        this.paymentStatusTimeout = setTimeout(() => {
          this.$store.dispatch(PAYMENT_GET_STATUS_BY_ID, {
            paymentId: this.paymentId
          })
          this.numberOfRepeats++
        }, 2000)
      } else {
        this.stopPaymentPolling()
        this.paymentStatusLoading = false
        this.showFeedbackModal = true
      }
    },
    closeFeedbackModal: function () {
      this.$router.push(this.$route.path)
      this.$store.dispatch(EVENT_DATES_GET, {
        dateId: this.dateId,
        securityHash: this.securityHash
      })
    },
    isAfterNow: function (datetime) {
      return moment(datetime).isAfter(moment())
    },
    getInitials: function (firstName, lastName) {
      return (
        (firstName ? firstName.charAt(0) : '') +
        (lastName ? lastName.charAt(0) : '')
      ).toUpperCase()
    },
    routeToProfileView: function (id) {
      this.$router.push('/profile-view/' + id)
    },
    routeToGroup: function () {
      if (this.groupId) {
        this.$router.push(`/groups/${this.groupId}`)
      }
    },
    trackShare: function () {
      analytics.capture('activity_shared', { method: 'whatsapp' })
    },
    routeToEventSignup: function () {
      analytics.capture('join_button_clicked', {
        event_id: this.eventDate?.eventId,
        group_id: this.eventDate?.groupId
      })
      this.$router.push(`/events/${this.dateId}/signup/${this.securityHash}`)
    },
    routeToEventEditView: function () {
      this.$router.push(
        `/event-edit/${this.eventDate.eventId}/${this.securityHash}`
      )
    },
    routeToParticipants: function () {
      this.$router.push(
        `/events/${this.dateId}/${this.securityHash}/participants`
      )
    },
    updateParticipantDate: function () {
      this.routeToEventSignup()
    },
    async joinWaitlist() {
      if (!this.authId) {
        this.$router.push({
          path: '/login',
          query: { redirectTo: this.$route.path }
        })
        return
      }
      try {
        await this.$store.dispatch(EVENT_JOIN_WAITLIST, {
          eventId: this.eventDate.eventId,
          dateIds: [parseInt(this.dateId)],
          securityHash: this.securityHash
        })
        this.showWaitlistModal = true
      } catch (e) {
        console.log('error joining waitlist', e)
      }
    },
    async leaveWaitlist() {
      try {
        await this.$store.dispatch(EVENT_LEAVE_WAITLIST, {
          eventId: this.eventDate.eventId,
          dateIds: [parseInt(this.dateId)],
          securityHash: this.securityHash
        })
      } catch (e) {
        console.log('error leaving waitlist', e)
      }
    }
  },
  metaInfo() {
    const e = this.eventDate
    if (this.dateStatus !== 'success' || !e || !e.name) return {}

    const city = (e.location && e.location.city) || ''
    const sport = (e.activity && e.activity.name) || 'Sport'
    const dateLabel = e.dateWithDay || e.date || ''
    const title = `${e.name}${city ? ` – ${city}` : ''} | Inviplay`
    const description = truncate(
      e.description ||
        `${sport}${dateLabel ? ` op ${dateLabel}` : ''}${
          city ? ` in ${city}` : ''
        }. Doe mee via Inviplay.`,
      160
    )
    const image = this.hasEventImage ? e.imageUrl : OG_DEFAULT_IMAGE
    const url = `${window.location.origin}/events/${this.dateId}/${this.securityHash}`

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: e.name,
      startDate: e.startDateTime,
      endDate: e.endDateTime,
      eventStatus: e.cancelled
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
      description,
      image,
      url,
      organizer: {
        '@type': 'Organization',
        name: 'Inviplay',
        url: 'https://www.inviplay.nl'
      }
    }
    if (e.location && e.location.name) {
      jsonLd.location = {
        '@type': 'Place',
        name: e.location.name,
        ...(city ? { address: city } : {})
      }
    }
    if (typeof e.costFeeIncluded === 'number') {
      jsonLd.offers = {
        '@type': 'Offer',
        price: e.costFeeIncluded,
        priceCurrency: 'EUR',
        url
      }
    }

    return {
      title,
      meta: [
        { vmid: 'description', name: 'description', content: description },
        { vmid: 'og:title', property: 'og:title', content: title },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: description
        },
        { vmid: 'og:image', property: 'og:image', content: image },
        { vmid: 'og:url', property: 'og:url', content: url },
        { vmid: 'og:type', property: 'og:type', content: 'event' },
        { vmid: 'twitter:title', name: 'twitter:title', content: title },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: description
        },
        { vmid: 'twitter:image', name: 'twitter:image', content: image }
      ],
      link: [{ vmid: 'canonical', rel: 'canonical', href: url }],
      script: [
        {
          vmid: 'ld-event',
          type: 'application/ld+json',
          json: jsonLd
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.event-detail-page {
  padding-bottom: 5rem;
  background: #f9fafb;
  min-height: 100vh;
}

/* Hero Image */
.hero-image-wrapper {
  position: relative;
  width: calc(100% + 30px);
  margin-top: -1rem;
  margin-left: -15px;
  margin-right: -15px;
  aspect-ratio: 3 / 1;
  background: #f3f4f6;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-image-fallback,
.hero-image-fallback-active {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba($ipGreen, 0.2),
    rgba($ipGreen, 0.05)
  );
}

.hero-fallback-icon {
  font-size: 4rem;
  color: $ipGreen;
  opacity: 0.5;
}

.hero-gradient {
  position: absolute;
  inset: auto 0 0 0;
  height: 5rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent);
}

.hero-sport-badge {
  position: absolute;
  bottom: 0.75rem;
  left: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.9);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Meta Card */
.meta-card {
  background: #fff;
  padding: 1.25rem;
  padding-bottom: 1rem;
  margin-left: -15px;
  margin-right: -15px;
}

.meta-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.meta-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.3;

  @media (min-width: 640px) {
    font-size: 1.45rem;
  }
}

.meta-card-group {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
  font-size: 0.84rem;
  color: $ipGreen;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.meta-card-share {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.edit-btn {
  height: 2.25rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid $ipGreen;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: $ipGreen;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #ecfdf5;
  }
}

.share-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 1rem;
  text-decoration: none;

  &:hover {
    border-color: $ipGreen;
    color: $ipGreen;
    background: #ecfdf5;
  }
}

/* Info Grid */
.info-grid {
  margin-top: 1rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.5rem 1rem;
}

.info-grid-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.info-grid-icon {
  flex-shrink: 0;
  width: 1rem;
  font-size: 0.875rem;
  color: $ipGreen;
}

.info-grid-text {
  font-size: 0.86rem;
  color: #374151;
  white-space: nowrap;
}

.info-grid-link {
  text-decoration: none;
  color: #374151;

  &:hover {
    color: $ipGreen;
  }
}

.free-badge {
  background: #ecfdf5;
  color: #059669;
  padding: 0 0.375rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  margin-left: 0.25rem;
}

/* Content Grid */
.content-grid {
  margin-top: 0.5rem;
  margin-left: -15px;
  margin-right: -15px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 280px;
  }
}

.content-left {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.content-card {
  background: #fff;
  padding: 1.25rem;
  padding-top: 1rem;
}

.content-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.details-text {
  font-size: 0.88rem;
  line-height: 1.7;
  color: #4b5563;
  margin: 0;
}

.participant-info-card {
  border-left: 3px solid #16a34a;
}

.participant-info-hint {
  font-size: 0.76rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

/* Messages */
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.message-item {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.message-avatar {
  flex-shrink: 0;
  margin-right: 0;
}

.show-more-messages-btn {
  background: transparent;
  border: none;
  color: $ipGreen;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.25rem 0;
  margin-bottom: 0.75rem;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
}

.message-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.message-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.82rem;
  color: #374151;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: $ipGreen;
  }
}

.message-send-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: $ipGreen;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: darken($ipGreen, 8%);
  }
}

/* Deelnemers */
.deelnemers-card {
  @media (min-width: 768px) {
    align-self: start;
  }
}

.deelnemers-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.deelnemer-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.deelnemer-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.deelnemer-avatar {
  flex-shrink: 0;
  margin-right: 0;
}

.initials-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #3a9e7e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.organizer-crown {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.organizer-badge {
  font-size: 0.65rem;
  color: #d97706;
  background: #fffbeb;
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
}

.deelnemer-current-user {
  background: rgba(236, 253, 245, 0.7);
  border-radius: 0.5rem;
  padding: 0.375rem 0.5rem;
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.current-user-avatar {
  box-shadow: 0 0 0 2px #3a9e7e, 0 0 0 3px white;
}

.current-user-badge {
  color: #3a9e7e;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 0 0.375rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  line-height: 1.4;
}

.show-all-link {
  color: $ipGreen;
  text-decoration: none;
  font-size: 0.82rem;
  transition: color 0.15s;

  &:hover {
    color: darken($ipGreen, 8%);
  }
}

.show-all-count {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $ipGreen;
  font-size: 0.72rem;
  flex-shrink: 0;
}

/* Waitlist */
.waitlist-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.waitlist-btn-off {
  width: 100%;
  padding: 0.625rem;
  border-radius: 0.75rem;
  border: none;
  background: #f59e0b;
  color: #fff;
  font-size: 0.86rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #d97706;
  }
}

.waitlist-btn-on {
  width: 100%;
  padding: 0.625rem;
  border-radius: 0.75rem;
  border: 2px solid #fbbf24;
  background: #fff;
  color: #d97706;
  font-size: 0.86rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #fffbeb;
  }
}

/* Sticky CTA */
.sticky-cta-container {
  position: fixed;
  bottom: 60px;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #f3f4f6;
  z-index: 10;
}

.sticky-cta-inner {
  padding-top: 0.75rem;
  padding-bottom: 1rem;
}

.cta-btn {
  display: block;
  width: 100%;
  padding: 0.75rem;
  border-radius: 9999px;
  background: $ipGreen;
  color: #fff;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s;

  &:hover {
    background: darken($ipGreen, 8%);
    color: #fff;
  }

  &:active {
    background: darken($ipGreen, 15%);
  }
}

/* Container overrides */
.container-event-view {
  padding-top: 0;
  padding-bottom: 15px;
}

.container-event-view.has-fixed-action-button {
  padding-bottom: 100px;
}
</style>
